import { Box, Container, SxProps, Theme, useTheme } from '@mui/material';
import { Children, ReactNode, useRef, useState } from 'react';

import { hasWindow } from 'utils/browser.utils';

import { useResizeObserver } from 'hooks/useResizeObserver';

import { CHEVRON_FONT_SIZE } from './constants';
import SliderArrow from './SliderArrow';

interface Props {
  minWidthItem: number;
  sx?: SxProps<Theme>;
  children: ReactNode;
  //we need this to correctly position the arrows
  imageRefHeight: number;
}

const GAP = 16;
const CONTAINER_WIDTH = 1200;
const CONTAINER_PADDING = 24;

const DesktopSliderList = ({
  minWidthItem,
  children,
  imageRefHeight,
}: Props) => {
  const items = Children.toArray(children);
  const { direction } = useTheme();
  const boxRef = useRef<HTMLDivElement>(null);
  const [x, setX] = useState(0);

  // Take ref of document.body if we have a window object
  const windowRef = useRef<HTMLElement>(
    hasWindow() ? window.document.body : null,
  );
  // width of window
  const [width] = useResizeObserver(windowRef);

  // Center icon inside image (image height / 2 - icon height / 2)
  const iconHeight = imageRefHeight / 2 - CHEVRON_FONT_SIZE / 2;

  const minWidthItemPlusGap = minWidthItem + GAP;
  const slidesInView =
    ((width - CONTAINER_WIDTH) / 2 + CONTAINER_WIDTH) / minWidthItemPlusGap;
  const maxScroll = (items.length - slidesInView) * minWidthItemPlusGap;
  //spacing of the material ui container on 1 side
  const containerSpacing = (width - 1200) / 2;
  // Add containerSpacing + padding if it's large screen otherwise just add gap
  const listWidth =
    minWidthItemPlusGap * items.length +
    (containerSpacing <= 0 ? GAP : containerSpacing + CONTAINER_PADDING);

  const handleNext = () => {
    const scrollByLeft =
      (direction === 'ltr' ? minWidthItemPlusGap : -minWidthItemPlusGap) * 2;
    boxRef.current?.scrollBy({
      left: scrollByLeft,
      behavior: 'smooth',
    });
  };

  const handlePrev = () => {
    const scrollByLeft =
      (direction === 'ltr' ? -minWidthItemPlusGap : minWidthItemPlusGap) * 2;
    boxRef.current?.scrollBy({
      left: scrollByLeft,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Box
        sx={{
          overflowX: 'scroll',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',
          //Added to remove scrollbar on FireFox
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        onScroll={(e) => setX(e.currentTarget?.scrollLeft)}
        ref={boxRef}
      >
        <Container
          sx={{
            display: { xs: 'none', md: 'block' },
            overflow: 'visible !important',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              gap: 2,
              width: listWidth,
            }}
          >
            {items.map((slide, key) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  flex: '1 auto',
                  minWidth: minWidthItem,
                  maxWidth: minWidthItem,
                  scrollSnapAlign: 'start',
                  scrollMargin: '0 16px',
                }}
              >
                {slide}
              </Box>
            ))}
          </Box>
          <SliderArrow
            direction="left"
            iconHeight={iconHeight}
            onClick={handlePrev}
            visibility={x === 0 ? 'hidden' : 'visible'}
          />
          <SliderArrow
            direction="right"
            iconHeight={iconHeight}
            onClick={handleNext}
            visibility={Math.abs(x) >= maxScroll ? 'hidden' : 'visible'}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default DesktopSliderList;
